// React Dependencies
import React, { useEffect, useRef, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button } from '@material-ui/core';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import PostsMainView from './postsMainView';
import PostsDialogView from './postsDialogView';
import { PositionType, SpecialHighlightPostType } from '../../../common/constants';
import Util, { Trim } from '../../../common/util';
import { IsEmpty } from '../../../common/checks';

import PostTable from '../posts/table';
import PostCreateModal from '../../post/postTypeModal';

const PageHighlightPosts = (props) => {
  const { pageId, pageHighlightId } = props.match.params;
  const [isSpecial, setIsSpecial] = useState(false);
  const [isExclusive, setIsExclusive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(20);
  const [postPageHighlights, setPostPageHighlights] = useState([]);

  // Exclusive Post Page Highlight
  const [redirectUrl, setRedirectUrl] = useState();
  const [openPostTypeModal, setOpenPostTypeModal] = useState(false);

  // Special Post Page Highlight
  const SortableCardRefSpecialPost = useRef();
  const [specialPostBrandSearch, setSpecialPostBrandSearch] = useState(null);
  const [refreshSpecialPost, setRefreshSpecialPost] = useState(false);
  const [pageNoSpecialPost, setPageNoSpecialPost] = useState(1);
  const [openAddSpecialPost, setOpenAddSpecialPost] = useState(false);
  const [specialPostUpdated, setSpecialPostUpdated] = useState([]);
  const [totalCountSpecialPost, setTotalCountSpecialPost] = useState(0);
  const [allSpecialPostCount, setAllSpecialPostCount] = useState(0);

  // Pic Marker Post
  const SortableCardRefPicMarkerPost = useRef();
  const [picMarkerPosts, setPicMarkerPosts] = useState([]);
  const [picMarkerPostBrandSearch, setPicMarkerPostBrandSearch] = useState(null);
  const [refreshPicMarkerPost, setRefreshPicMarkerPost] = useState(false);
  const [pageNoPicMarkerPost, setPageNoPicMarkerPost] = useState(1);
  const [openAddPicMarkerPost, setOpenAddPicMarkerPost] = useState(false);
  const [picMarkerPostUpdated, setPicMarkerPostUpdated] = useState([]);
  const [totalCountPicMarkerPost, setTotalCountPicMarkerPost] = useState(0);
  const [allPicMarkerPostCount, setAllPicMarkerPostCount] = useState(0);

  // Pinned Post
  const SortableCardRefPinnedPost = useRef();
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [pinnedPostBrandSearch, setPinnedPostBrandSearch] = useState(null);
  const [refreshPinnedPost, setRefreshPinnedPost] = useState(false);
  const [pageNoPinnedPost, setPageNoPinnedPost] = useState(1);
  const [openAddPinnedPost, setOpenAddPinnedPost] = useState(false);
  const [pinnedPostUpdated, setPinnedPostUpdated] = useState([]);
  const [totalCountPinnedPost, setTotalCountPinnedPost] = useState(0);
  const [allPinnedPostCount, setAllPinnedPostCount] = useState(0);

  const createPost = async (data) => {
    try {
      const payload = {
        pageId,
        type:      data.postType,
        promoType: data.promoType,
      };
      const post = await apiService.addPost(payload);
      toast.success('Post created');

      await apiService.addPostPageHighlight(pageId, pageHighlightId, post.id);
      toast.success('Post added to highlight');

      setOpenPostTypeModal(false);
      setRedirectUrl(`/admin/page/${pageId}/post/${post.id}?pageHighlightId=${pageHighlightId}`);
    } catch (err) {
      toast.error(err);
    }
  };

  const fetchApi = async () => {
    try {
      // For Special & Exclusive Post Page Highlight
      const postPageHighlightData = await apiService.getPostPageHighlights({ pageId, pageHighlightId });
      setPostPageHighlights(postPageHighlightData.rows);

      // Special Post Page Highlight
      if (isSpecial) {
        SortableCardRefSpecialPost.current.updateItems(postPageHighlightData.rows);
      }

      // Pic Marker Post
      const picMarkerData = await apiService.getPostPageHighlights({ pageId, pageHighlightId, isPicMarker: true });
      setPicMarkerPosts(picMarkerData.rows);
      SortableCardRefPicMarkerPost.current.updateItems(picMarkerData.rows);

      // Pinned Post
      const pinnedPostData = await apiService.getPinnedPosts({ pageId, pageHighlightId });
      setPinnedPosts(pinnedPostData);
      SortableCardRefPinnedPost.current.updateItems(pinnedPostData);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onDelete = async (postId, type) => {
    try {
      setLoading(true);
      let res;

      if (type === SpecialHighlightPostType.Special) {
        res = await apiService.removePostPageHighlightPositionType(pageId, pageHighlightId, postId);
      } else if (type === SpecialHighlightPostType.PicMarker) {
        const postRemoved = picMarkerPosts.filter((p) => p.postId === postId);
        res = await apiService.updatePostPageHighlight(postRemoved[0].id, { pageId, isPicMarker: false });
      } else if (type === SpecialHighlightPostType.Pinned) {
        const postRemoved = pinnedPosts.filter((p) => p.postId === postId);
        res = await apiService.removePinnedPostPositionType(pageId, pageHighlightId, postRemoved[0].postPageHighlightId);
      }

      if (res) {
        toast.success('Post removed');

        fetchApi();
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onClickAdd = (type) => {
    if (type === SpecialHighlightPostType.Special) {
      setRefreshSpecialPost(true);
      setSpecialPostBrandSearch(null);
      setPageNoSpecialPost(1);
      setOpenAddSpecialPost(true);
    } else if (type === SpecialHighlightPostType.PicMarker) {
      setRefreshPicMarkerPost(true);
      setPicMarkerPostBrandSearch(null);
      setPageNoPicMarkerPost(1);
      setOpenAddPicMarkerPost(true);
    } else if (type === SpecialHighlightPostType.Pinned) {
      setRefreshPinnedPost(true);
      setPinnedPostBrandSearch(null);
      setPageNoPinnedPost(1);
      setOpenAddPinnedPost(true);
    }
  };

  const handleCloseAdd = (type) => {
    if (type === SpecialHighlightPostType.Special) {
      setOpenAddSpecialPost(false);
      setRefreshSpecialPost(true);
    } else if (type === SpecialHighlightPostType.PicMarker) {
      setOpenAddPicMarkerPost(false);
      setRefreshPicMarkerPost(true);
    } else if (type === SpecialHighlightPostType.Pinned) {
      setOpenAddPinnedPost(false);
      setRefreshPinnedPost(true);
    }
  };

  const onAdd = async (data, type) => {
    try {
      let res;
      let pinnedPosition = 1;
      let successMessage = 'Successfully added';

      if (type === SpecialHighlightPostType.Special) {
        res = await apiService.addPostPageHighlight(pageId, pageHighlightId, data.postId);

        const filterPosts = specialPostUpdated.filter((p) => p.id !== data.postId);
        setSpecialPostUpdated(filterPosts);
      } else if (type === SpecialHighlightPostType.PicMarker) {
        const postAdded = picMarkerPostUpdated.filter((p) => p.id === data.postId);
        res = await apiService.updatePostPageHighlight(postAdded[0].id, { pageId, pageHighlightId, isPicMarker: true });

        const filterPosts = picMarkerPostUpdated.filter((p) => p.id !== data.postId);
        setPicMarkerPostUpdated(filterPosts);
      } else if (type === SpecialHighlightPostType.Pinned) {
        if (pinnedPosts.length > 0) {
          const maxPinnedPosition = _.maxBy(pinnedPosts, 'position');
          pinnedPosition = maxPinnedPosition.position + 1;
        }

        const postAdded = pinnedPostUpdated.filter((p) => p.id === data.postId);
        res = await apiService.addPinnedPost(pageId, pageHighlightId, postAdded[0].id, pinnedPosition, PositionType.Rotatable);

        const filterPosts = pinnedPostUpdated.filter((p) => p.id !== data.postId);
        setPinnedPostUpdated(filterPosts);
        successMessage = 'Successfully pinned';
      }

      if (res) {
        toast.success(successMessage);
        fetchApi();
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const updateSeq = async (data) => {
    try {
      setLoading(true);
      const res = await apiService.updatePinnedPostOrderPosition(pageId, pageHighlightId, data.postPageHighlightIds);

      if (res) {
        toast.success('Position updated');
        fetchApi();
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onSortableCardEvent = async (eventData) => {
    const newOrdering = eventData.data.new.map((v) => v.postPageHighlightId);
    const postsSorted = { postPageHighlightIds: newOrdering };
    updateSeq(postsSorted);
  };

  const onUpdatePosType = async (post) => {
    try {
      setLoading(true);
      const postUpdated = pinnedPosts.filter((p) => p.postId === post.postId);
      const res = await apiService.updatePinnedPostPositionType(pageId, pageHighlightId, postUpdated[0].postPageHighlightId, post.positionType);

      if (res) {
        toast.success('Position type updated');

        fetchApi();
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onSearchSpecialPost = async () => {
    try {
      const mapExistingOfPinnedPostIds = postPageHighlights.map((e) => e.postId);
      const postBrandKey = Trim(specialPostBrandSearch, false, true);

      setLoading(true);
      const data = await apiService.getPostsByBrandAndCategoryGroup({
        brand:         postBrandKey,
        categoryGroup: 'All',
        pageSize,
        pageId,
        pageNo:        pageNoSpecialPost - 1,
        orderCol:      'id',
        orderDir:      'DESC',
      });

      setAllSpecialPostCount(data.totalCount);
      setTotalCountSpecialPost(Math.ceil(data.totalCount / pageSize));

      const updatedData = _.map(data.rows, (d) => _.assign({
        ...d,
        isPinned: mapExistingOfPinnedPostIds.includes(d.id),
      }));
      setSpecialPostUpdated(updatedData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onSearchPicMarkerPost = async () => {
    try {
      setLoading(true);
      const data = postPageHighlights;
      setAllPicMarkerPostCount(data.length);
      setTotalCountPicMarkerPost(Math.ceil(data.length / pageSize));

      const updatedData = _.map(data, (d) => _.assign({
        ...d,
        isPinned: d.isPicMarker,
      }));
      setPicMarkerPostUpdated(updatedData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onSearchPinnedPost = async () => {
    try {
      const mapExistingOfPinnedPostIds = pinnedPosts.map((e) => e.postId);

      setLoading(true);
      const data = postPageHighlights;
      setAllPinnedPostCount(data.length);
      setTotalCountPinnedPost(Math.ceil(data.length / pageSize));

      const updatedData = _.map(data, (d) => _.assign({
        ...d,
        isPinned: mapExistingOfPinnedPostIds.includes(d.postId),
      }));
      setPinnedPostUpdated(updatedData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  useEffect(() => {
    const query = Util.ExtractParamsFromUrlQueryStr(props.location.search);
    if (!IsEmpty(query)) {
        setIsSpecial(query.isSpecial === 'true' || false);
        setIsExclusive(query.isExclusive === 'true' || false);
    }
  }, []);

  useEffect(() => {
    fetchApi();
  }, [isSpecial, isExclusive]);

  useEffect(() => {
    if (refreshSpecialPost || refreshPicMarkerPost || refreshPinnedPost) {
      fetchApi();
    }
  }, [refreshSpecialPost, refreshPicMarkerPost, refreshPinnedPost]);

  useEffect(() => {
    if (openAddSpecialPost) {
      onSearchSpecialPost();
    }
  }, [openAddSpecialPost, specialPostBrandSearch, pageNoSpecialPost]);

  useEffect(() => {
    if (openAddPicMarkerPost) {
      onSearchPicMarkerPost();
    }
  }, [openAddPicMarkerPost, picMarkerPostBrandSearch, pageNoPicMarkerPost]);

  useEffect(() => {
    if (openAddPinnedPost) {
      onSearchPinnedPost();
    }
  }, [openAddPinnedPost, pinnedPostBrandSearch, pageNoPinnedPost]);

  return redirectUrl ? <Redirect to={redirectUrl} /> : (
    <>
      <div className="row" style={{}}>
        <div className="col-md-12">

          {/* Post Page Highlight */}
          <div className="row" style={{ minHeight: '30vh' }}>
            {
              isSpecial
                ? (
                  <PostsMainView
                    title="Special Post(s)"
                    sortableCardRef={SortableCardRefSpecialPost}
                    onClickAdd={() => onClickAdd(SpecialHighlightPostType.Special)}
                    onDelete={(postId) => onDelete(postId, SpecialHighlightPostType.Special)}
                    cards={postPageHighlights}
                    isSortable={false}
                    cardype="Special"
                  />
                ) : (
                  <>
                    <Button
                      style={{ marginLeft: 15, marginBottom: 20 }}
                      onClick={() => setOpenPostTypeModal(true)}
                      variant="contained"
                      color="primary"
                      size="medium"
                    >
                      Add Post
                    </Button>
                    <div className="col-lg-12 col-12" style={{ marginBottom: 20 }}>
                      <PostTable pageId={pageId} pageHighlightId={pageHighlightId} />
                    </div>
                  </>
                )
            }
          </div>

          {/* Pic Marker Post */}
          <div className="row" style={{ minHeight: '30vh', borderTop: '1px solid black', marginTop: 20, paddingTop: 20 }}>
            <PostsMainView
              title="Post(s) showing as picture-marker in Deals Map"
              sortableCardRef={SortableCardRefPicMarkerPost}
              onClickAdd={() => onClickAdd(SpecialHighlightPostType.PicMarker)}
              onDelete={(postId) => onDelete(postId, SpecialHighlightPostType.PicMarker)}
              cards={picMarkerPosts}
              isSortable={false}
              cardype="Pic Marker"
            />
          </div>

          {/* Pinned Post */}
          <div className="row" style={{ minHeight: '30vh', borderTop: '1px solid black', marginTop: 20, paddingTop: 20 }}>
            <PostsMainView
              title="Pinned Post(s) (Click-and-drag to re-order position)"
              sortableCardRef={SortableCardRefPinnedPost}
              onEvent={(eventData) => onSortableCardEvent(eventData)}
              onClickAdd={() => onClickAdd(SpecialHighlightPostType.Pinned)}
              onDelete={(postId) => onDelete(postId, SpecialHighlightPostType.Pinned)}
              onUpdatePosType={(post) => onUpdatePosType(post)}
              cards={pinnedPosts}
              isSortable
              cardype="Pinned"
            />
          </div>
        </div>

        {/* Special Post Page Highlight */}
        {isSpecial && (
          <PostsDialogView
            loading={loading}
            allPostCount={allSpecialPostCount}
            totalCount={totalCountSpecialPost}
            pageNo={pageNoSpecialPost}
            posts={postPageHighlights}
            postsUpdated={specialPostUpdated}
            openAdd={openAddSpecialPost}
            onAdd={(data) => onAdd(data, SpecialHighlightPostType.Special)}
            handleClose={() => handleCloseAdd(SpecialHighlightPostType.Special)}
            setPageNo={setPageNoSpecialPost}
            setBrandSearch={setSpecialPostBrandSearch}
            isPinLabel={false}
          />
        )}

        {/* Pic Marker Post */}
        <PostsDialogView
          loading={loading}
          allPostCount={allPicMarkerPostCount}
          totalCount={totalCountPicMarkerPost}
          pageNo={pageNoPicMarkerPost}
          posts={picMarkerPosts}
          postsUpdated={picMarkerPostUpdated}
          openAdd={openAddPicMarkerPost}
          onAdd={(data) => onAdd(data, SpecialHighlightPostType.PicMarker)}
          handleClose={() => handleCloseAdd(SpecialHighlightPostType.PicMarker)}
          setPageNo={setPageNoPicMarkerPost}
          setBrandSearch={setPicMarkerPostBrandSearch}
          isPinLabel={false}
        />

        {/* Pinned Post */}
        <PostsDialogView
          loading={loading}
          allPostCount={allPinnedPostCount}
          totalCount={totalCountPinnedPost}
          pageNo={pageNoPinnedPost}
          posts={pinnedPosts}
          postsUpdated={pinnedPostUpdated}
          openAdd={openAddPinnedPost}
          onAdd={(data) => onAdd(data, SpecialHighlightPostType.Pinned)}
          handleClose={() => handleCloseAdd(SpecialHighlightPostType.Pinned)}
          setPageNo={setPageNoPinnedPost}
          setBrandSearch={setPinnedPostBrandSearch}
          isPinLabel
        />
      </div>

      {
        openPostTypeModal && <PostCreateModal onComplete={(d) => createPost(d)} onClose={() => setOpenPostTypeModal(false)} />
      }
    </>
  );
};

PageHighlightPosts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      pageId:          PropTypes.string.isRequired,
      pageHighlightId: PropTypes.string.isRequired,
    }),
  }).isRequired,

  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default connect(null, { })(PageHighlightPosts);
